/**
 * Human-readable text for a live WebSocket event.
 *
 * The socket delivers `{ eventType, payload }` with an opaque payload; the
 * notification store keeps a plain `message` string. Stringifying the
 * payload at ingest showed clinicians raw JSON blobs — this formats the
 * known shapes instead, with a compact `key: value` fallback for the rest
 * so an unfamiliar event still reads as words, never braces.
 *
 * Notification CONTENT, not chrome: templates stay in English like the
 * persisted notification titles the inbox already renders verbatim.
 */

function humanize(token: string): string {
  const words = token.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : token;
}

function isScalar(value: unknown): value is string | number | boolean {
  const t = typeof value;
  return t === 'string' || t === 'number' || t === 'boolean';
}

/** Compact `key: value` facts for scalar payload entries. */
function factList(payload: Record<string, unknown>, exclude: ReadonlySet<string>): string {
  return Object.entries(payload)
    .filter(([k, v]) => !exclude.has(k) && isScalar(v))
    .map(([k, v]) => `${humanize(k)}: ${String(v)}`)
    .join(', ');
}

const MESSAGE_ONLY = new Set(['message']);
const CRITICAL_CONSUMED = new Set(['message', 'resultId', 'patientId']);

export function formatWsEventMessage(eventType: string, payload: unknown): string {
  // A bare string payload is already the message.
  if (typeof payload === 'string') return payload.trim() || humanize(eventType);

  // A producer-supplied message always wins (system notices, pages).
  const direct = (payload as Record<string, unknown> | null | undefined)?.message;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();

  if (!payload || typeof payload !== 'object') return humanize(eventType);
  const body = payload as Record<string, unknown>;

  switch (eventType) {
    case 'critical_result': {
      const facts: string[] = [];
      if (body.resultId != null) facts.push(`result ${String(body.resultId)}`);
      if (body.patientId != null) facts.push(`patient ${String(body.patientId)}`);
      const rest = factList(body, CRITICAL_CONSUMED);
      const extra = [...facts, ...(rest ? [rest] : [])].join(', ');
      return extra ? `Critical result — ${extra}` : 'Critical result';
    }
    case 'cds_alert': {
      const rest = factList(body, MESSAGE_ONLY);
      return rest ? `CDS alert — ${rest}` : 'CDS alert';
    }
    default: {
      const rest = factList(body, MESSAGE_ONLY);
      return rest ? `${humanize(eventType)} — ${rest}` : humanize(eventType);
    }
  }
}
